/**
 * Attention when the app is in the background: a short chime for new messages (a different one when
 * someone @mentions you) and the unread count drawn on the tab icon. Sound can be switched off per device.
 * The chime is synthesised with Web Audio, so there is no sound file to download.
 */
import { badgeOverlay, desktop } from './desktop.ts';
import { setNativeBadge } from './native.ts';

const SOUND_KEY = 'tc.sound';

export function soundEnabled(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
}
export function setSoundEnabled(on: boolean): void {
  try {
    if (on) localStorage.removeItem(SOUND_KEY);
    else localStorage.setItem(SOUND_KEY, 'off');
  } catch {
    /* private mode */
  }
}

let ctx: AudioContext | null = null;
function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  ctx ??= new AC();
  return ctx;
}
// Browsers only allow sound after the person has interacted with the page once.
if (typeof window !== 'undefined') {
  const unlock = () => void audio()?.resume().catch(() => {});
  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock);
}

let lastChime = 0;
/** Two soft notes; a mention gets a brighter three-note chime. At most one chime every 2 seconds. */
export function chime(kind: 'message' | 'mention' = 'message', force = false): void {
  if (!force && !soundEnabled()) return;
  const now = Date.now();
  if (!force && now - lastChime < 2000) return;
  lastChime = now;
  const ac = audio();
  if (!ac || ac.state !== 'running') return;
  const notes = kind === 'mention' ? [880, 1175, 1568] : [784, 1047];
  const t0 = ac.currentTime + 0.01;
  notes.forEach((freq, i) => {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const start = t0 + i * 0.12;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.18, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
    osc.connect(gain).connect(ac.destination);
    osc.start(start);
    osc.stop(start + 0.4);
  });
}

/** Should a new message make a sound? Only when the person is not looking at that chat right now. */
export function needsAttention(conversationId: string, activeId: string | null): boolean {
  if (typeof document === 'undefined') return false;
  return document.hidden || !document.hasFocus() || activeId !== conversationId;
}

// ---- unread count on the tab icon --------------------------------------------------------------
let baseIcon: HTMLImageElement | null = null;
let lastBadge = -1;
const originals = new Map<HTMLLinkElement, string>();

export function setIconBadge(count: number): void {
  if (typeof document === 'undefined' || count === lastBadge) return;
  lastBadge = count;
  // Phone app: number on the app icon.
  setNativeBadge(Math.max(0, count));
  // Desktop app: Dock (Mac) / taskbar (Windows).
  if (desktop) desktop.setBadge(Math.max(0, count), badgeOverlay(count));
  // Same number on the app icon when installed (home screen / dock). Ignored where unsupported.
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try {
    if (count > 0) void nav.setAppBadge?.(count).catch(() => {});
    else void nav.clearAppBadge?.().catch(() => {});
  } catch {
    /* not supported */
  }
  const links = [...document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')];
  for (const l of links) if (!originals.has(l)) originals.set(l, l.href);
  if (count <= 0) {
    for (const [l, href] of originals) l.href = href;
    return;
  }
  const draw = () => {
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const g = c.getContext('2d');
    if (!g || !baseIcon) return;
    g.drawImage(baseIcon, 0, 0, 64, 64);
    const label = count > 9 ? '9+' : String(count);
    g.fillStyle = '#e5322d';
    g.beginPath();
    g.arc(44, 20, 20, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#ffffff';
    g.font = `bold ${label.length > 1 ? 22 : 28}px system-ui, sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(label, 44, 21);
    const url = c.toDataURL('image/png');
    for (const l of links) l.href = url;
  };
  if (baseIcon?.complete) return draw();
  baseIcon = new Image();
  baseIcon.onload = draw;
  baseIcon.src = '/favicon-64.png?v=2';
}
